import { useUsersOptions, type UseUsersOptionsParams } from '@components/ModelSelect/AsyncUserSelect/useUsersOptions';
import { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useQueryClient } from '@tanstack/react-query';

import type { Nullable } from 'chaika-frontend-types/frontend-cms';

import { primeUserListByIdsCache } from '@user/queries/userQueries';

import { AsyncMultiSelect, OptionType } from '@components/Select';
import { isDefined } from '@utils/isDefined';

interface Props extends Omit<UseUsersOptionsParams, 'selectedUserIds' | 'withAnyOption'> {
    dataTest?: string;
    disabled?: boolean;
    /** Controlled value. When set, the parent owns the selection state. */
    value?: string[];
    /**
     * Uncontrolled initial value. When set, the component owns the selection
     * state and `onChange` is the only signal the parent receives.
     * Cannot be combined with `value` (controlled mode).
     */
    defaultValue?: string[];
    title?: string;
    onChange(userIds: string[]): void;
    sortUsersWith?(managers: OptionType[]): OptionType[];
}

export const AsyncUserMultiSelect = observer(
    ({ dataTest, title, value, defaultValue, disabled, onChange, sortUsersWith, ...props }: Props) => {
        const controlled = isDefined(value);

        if (controlled && isDefined(defaultValue)) {
            throw new Error(
                `Invariant violation: AsyncUserMultiSelect received both 'value' and 'defaultValue'. Use 'value' for controlled mode (parent owns state) or 'defaultValue' for uncontrolled mode (component owns state), never both.`,
            );
        }

        const [internalValue, setInternalValue] = useState<string[]>(defaultValue ?? []);
        const currentValue = controlled ? value : internalValue;

        const { defaultOptions, loadOptions, selectedOptions } = useUsersOptions({
            ...props,
            selectedUserIds: currentValue,
        });

        const queryClient = useQueryClient();
        const handleChange = useCallback(
            (options: Nullable<OptionType<string>[]>) => {
                const selected = options?.map(({ value: id }) => id) ?? [];
                if (!controlled) {
                    setInternalValue(selected);
                }
                onChange(selected);
                primeUserListByIdsCache(queryClient, selected);
            },
            [controlled, onChange, queryClient],
        );

        const decoratedDefaultOptions = sortUsersWith ? sortUsersWith(defaultOptions) : defaultOptions;
        const decoratedLoadOptions = useCallback(
            async (name: string) => {
                const opts = await loadOptions(name);
                return sortUsersWith ? sortUsersWith(opts) : opts;
            },
            [loadOptions, sortUsersWith],
        );

        return (
            <AsyncMultiSelect
                dataTest={dataTest ?? 'AsyncUserMultiSelect'}
                disabled={disabled}
                title={title}
                value={selectedOptions}
                defaultOptions={decoratedDefaultOptions}
                loadOptions={decoratedLoadOptions}
                onChange={handleChange}
            />
        );
    },
);
